import { useRememberDocument } from '@/contexts/DocumentWorkspace';
import { useIncomingDocument } from '@/contexts/DocumentWorkspace';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  Upload,
  Droplets,
  Type,
  Image,
  Loader2,
  Download,
  Eye,
} from 'lucide-react';
import { PDFDocument, rgb, degrees, StandardFonts } from 'pdf-lib';
import { pdfjsLib } from '@/lib/pdfWorker';
import { readPdfLibBytes, pdfIntakeErrorMessage } from '@/lib/pdfIntake';
import { parsePageRange, formatFileSize } from '@/lib/pdfOperations';
import { useFileDialog } from '@/hooks/useFileDialog';
import CloudSourceHint from '@/components/CloudSourceHint';
import PageCanvas from '@/components/PageCanvas';
import { ProGate } from '@/components/ProBadge';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

const COLORS = [
  { hex: '#000000', label: 'Black' },
  { hex: '#FF0000', label: 'Red' },
  { hex: '#2196F3', label: 'Blue' },
  { hex: '#4CAF50', label: 'Green' },
  { hex: '#FF9800', label: 'Orange' },
  { hex: '#9C27B0', label: 'Purple' },
  { hex: '#607D8B', label: 'Gray' },
];

const POSITIONS = [
  { id: 'center', label: 'Center' },
  { id: 'top-left', label: 'Top Left' },
  { id: 'top-right', label: 'Top Right' },
  { id: 'bottom-left', label: 'Bottom Left' },
  { id: 'bottom-right', label: 'Bottom Right' },
  { id: 'tile', label: 'Tile / Repeat' },
];

const PAGE_MODES = [
  { id: 'all', label: 'All Pages' },
  { id: 'first', label: 'First Page Only' },
  { id: 'custom', label: 'Custom Range' },
];

function hexToRgbValues(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return { r, g, b };
}

/**
 * Watermark — Add text or image watermarks to PDF pages.
 */
export default function Watermark() {
  const navigate = useNavigate();
  const rememberDocument = useRememberDocument();
  const { openFile } = useFileDialog();
  const previewCanvasRef = useRef(null);

  // File state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Tab
  const [tab, setTab] = useState('text'); // 'text' | 'image'

  // Text watermark settings
  const [text, setText] = useState('CONFIDENTIAL');
  const [fontSize, setFontSize] = useState(48);
  const [color, setColor] = useState('#000000');
  const [customColor, setCustomColor] = useState('#000000');
  const [opacity, setOpacity] = useState(0.3);
  const [rotation, setRotation] = useState(-45);
  const [position, setPosition] = useState('center');

  // Image watermark settings
  const [wmImage, setWmImage] = useState(null); // { bytes, name, width, height, type }
  const [imageScale, setImageScale] = useState(50);
  const [imageOpacity, setImageOpacity] = useState(0.3);
  const [imagePosition, setImagePosition] = useState('center');
  const [imageRotation, setImageRotation] = useState(0);

  // Apply options
  const [pageMode, setPageMode] = useState('all');
  const [customRange, setCustomRange] = useState('');
  const [placement, setPlacement] = useState('on-top'); // 'behind' | 'on-top'

  // Processing state
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { blob, size }
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);

  // ==================== File handling ====================

  const loadFile = useCallback(async (file) => {
    if (!file || file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) return;
    if (pdfDoc) pdfDoc.destroy();

    setError(null);
    setResult(null);
    setFileName(file.name);
    setFileSize(file.size);

    try {
      // Decrypt owner-password PDFs (lossless) / reject truly locked ones
      // before pdf-lib ever sees the bytes — see src/lib/pdfIntake.js.
      const buf = await readPdfLibBytes(file);
      setArrayBuffer(buf);
      const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
      setPdfDoc(doc);
      rememberDocument(file);
      setNumPages(doc.numPages);
    } catch (err) {
      setError(pdfIntakeErrorMessage(err, 'add a watermark'));
    }
  }, [pdfDoc, rememberDocument]);

  useIncomingDocument(loadFile);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    loadFile(e.dataTransfer.files[0]);
  }, [loadFile]);

  const handleBrowse = useCallback(async () => {
    const result = await openFile('.pdf');
    if (result) {
      const file = new File([result.buffer], result.name, { type: 'application/pdf' });
      loadFile(file);
    }
  }, [openFile, loadFile]);

  // ==================== Image watermark upload ====================

  const handleBrowseImage = useCallback(async () => {
    const result = await openFile('image/*');
    if (!result) return;

    const bytes = new Uint8Array(result.buffer);
    const blob = new Blob([bytes]);
    const img = new window.Image();
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      setWmImage((prev) => {
        // Revoke the previous preview URL before swapping it — otherwise
        // every image pick leaks a blob reference into the session.
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return {
          bytes,
          name: result.name,
          width: img.naturalWidth,
          height: img.naturalHeight,
          type: blob.type || 'image/png',
          url,
        };
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, [openFile]);

  // Release the last preview URL on unmount so the Blob isn't retained
  // when the user navigates away mid-edit.
  useEffect(() => {
    return () => {
      if (wmImage?.url) URL.revokeObjectURL(wmImage.url);
    };
    // wmImage is captured once per effect re-run; the cleanup sees the
    // latest value because the effect re-registers whenever wmImage.url
    // changes.
  }, [wmImage?.url]);

  // ==================== Live preview ====================

  const activeColor = COLORS.find(c => c.hex === color) ? color : customColor;

  // Preview scale: fit page into ~400px wide panel
  const previewScaleRef = useRef(1);

  useEffect(() => {
    if (!pdfDoc || !previewCanvasRef.current) return;

    let cancelled = false;

    (async () => {
      const page = await pdfDoc.getPage(1);
      // Get the native PDF size (in points) to compute a fitting scale
      const nativeViewport = page.getViewport({ scale: 1 });
      const targetWidth = 400;
      const previewScale = targetWidth / nativeViewport.width;
      previewScaleRef.current = previewScale;

      const viewport = page.getViewport({ scale: previewScale });
      const canvas = previewCanvasRef.current;
      if (!canvas || cancelled) return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Render PDF page
      await page.render({ canvasContext: ctx, viewport }).promise;
      if (cancelled) return;

      // Draw watermark overlay — scale all sizes/positions proportionally
      const s = previewScale; // ratio: canvas pixels per PDF point
      if (tab === 'text' && text.trim()) {
        drawTextWatermarkPreview(ctx, viewport.width, viewport.height, s);
      } else if (tab === 'image' && wmImage) {
        await drawImageWatermarkPreview(ctx, viewport.width, viewport.height, s);
      }
    })();

    return () => { cancelled = true; };
  }, [pdfDoc, tab, text, fontSize, activeColor, opacity, rotation, position,
      wmImage, imageScale, imageOpacity, imagePosition, imageRotation, placement]);

  function drawTextWatermarkPreview(ctx, pw, ph, s) {
    const scaledFontSize = fontSize * s;
    ctx.save();
    ctx.globalAlpha = placement === 'behind' ? opacity * 0.3 : opacity;
    ctx.font = `${scaledFontSize}px Helvetica, Arial, sans-serif`;
    ctx.fillStyle = activeColor;

    const metrics = ctx.measureText(text);
    const tw = metrics.width;
    const th = scaledFontSize;

    if (position === 'tile') {
      const spacingX = tw + 60 * s;
      const spacingY = th + 60 * s;
      for (let y = -ph; y < ph * 2; y += spacingY) {
        for (let x = -pw; x < pw * 2; x += spacingX) {
          ctx.save();
          ctx.translate(x + tw / 2, y + th / 2);
          ctx.rotate((rotation * Math.PI) / 180);
          ctx.fillText(text, -tw / 2, th / 4);
          ctx.restore();
        }
      }
    } else {
      const { x, y } = getPreviewPosition(position, pw, ph, tw, th, s, rotation);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.fillText(text, -tw / 2, th / 4);
      ctx.restore();
    }
    ctx.restore();
  }

  async function drawImageWatermarkPreview(ctx, pw, ph, s) {
    if (!wmImage) return;
    const img = new window.Image();
    img.src = wmImage.url;
    await new Promise((resolve) => { img.onload = resolve; });

    // Match the pdf-lib sizing logic, then scale to preview
    const nativePw = pw / s;
    const nativePh = ph / s;
    const scaleFactor = imageScale / 100;
    const maxDim = Math.min(nativePw, nativePh);
    const aspect = wmImage.width / wmImage.height;
    let nativeW = maxDim * scaleFactor;
    let nativeH = nativeW / aspect;
    if (nativeH > maxDim * scaleFactor) {
      nativeH = maxDim * scaleFactor;
      nativeW = nativeH * aspect;
    }
    const drawW = nativeW * s;
    const drawH = nativeH * s;

    ctx.save();
    ctx.globalAlpha = placement === 'behind' ? imageOpacity * 0.3 : imageOpacity;

    if (imagePosition === 'tile') {
      const spacingX = drawW + 40 * s;
      const spacingY = drawH + 40 * s;
      for (let y = -ph; y < ph * 2; y += spacingY) {
        for (let x = -pw; x < pw * 2; x += spacingX) {
          ctx.save();
          ctx.translate(x + drawW / 2, y + drawH / 2);
          ctx.rotate((imageRotation * Math.PI) / 180);
          ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
          ctx.restore();
        }
      }
    } else {
      const { x, y } = getPreviewPosition(imagePosition, pw, ph, drawW, drawH, s, imageRotation);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((imageRotation * Math.PI) / 180);
      ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
      ctx.restore();
    }
    ctx.restore();
  }

  // Canvas preview coords (origin top-left, y goes down)
  // Uses rotated bounds for corner positions to prevent clipping
  function getPreviewPosition(pos, pw, ph, ew, eh, s, angleDeg = 0) {
    const margin = 40 * s;
    const rad = Math.abs((angleDeg || 0) * Math.PI / 180);
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const rw = ew * cos + eh * sin;
    const rh = ew * sin + eh * cos;
    switch (pos) {
      case 'top-left': return { x: margin + rw / 2, y: margin + rh / 2 };
      case 'top-right': return { x: pw - margin - rw / 2, y: margin + rh / 2 };
      case 'bottom-left': return { x: margin + rw / 2, y: ph - margin - rh / 2 };
      case 'bottom-right': return { x: pw - margin - rw / 2, y: ph - margin - rh / 2 };
      case 'center':
      default:
        return { x: pw / 2, y: ph / 2 };
    }
  }

  // ==================== Apply watermark with pdf-lib ====================

  const handleApply = useCallback(async () => {
    if (!arrayBuffer) return;

    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }

    setProcessing(true);
    setProgress(0);
    setError(null);
    setResult(null);

    try {
      const pdfLibDoc = await PDFDocument.load(arrayBuffer);
      const pages = pdfLibDoc.getPages();
      const totalPages = pages.length;

      // Determine which pages to watermark
      let targetPages;
      if (pageMode === 'first') {
        targetPages = [0];
      } else if (pageMode === 'custom' && customRange.trim()) {
        targetPages = parsePageRange(customRange, totalPages).map(p => p - 1);
      } else {
        targetPages = pages.map((_, i) => i);
      }

      // Embed font for text watermarks
      const font = await pdfLibDoc.embedFont(StandardFonts.Helvetica);

      // Embed image for image watermarks
      let embeddedImage = null;
      if (tab === 'image' && wmImage) {
        if (wmImage.type === 'image/png' || wmImage.name.endsWith('.png')) {
          embeddedImage = await pdfLibDoc.embedPng(wmImage.bytes);
        } else {
          embeddedImage = await pdfLibDoc.embedJpg(wmImage.bytes);
        }
      }

      for (let i = 0; i < targetPages.length; i++) {
        const pageIdx = targetPages[i];
        const page = pages[pageIdx];
        const { width: pw, height: ph } = page.getSize();

        if (tab === 'text' && text.trim()) {
          drawTextWatermarkPdfLib(page, font, pw, ph);
        } else if (tab === 'image' && embeddedImage) {
          drawImageWatermarkPdfLib(page, embeddedImage, pw, ph);
        }

        setProgress((i + 1) / targetPages.length);
      }

      const resultBytes = await pdfLibDoc.save();
      const blob = new Blob([resultBytes], { type: 'application/pdf' });
      setResult({ blob, size: resultBytes.length });
      if (usageCapApplies()) incrementUsage();
      setProgress(1);
    } catch (err) {
      setError(`Watermark failed: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  }, [arrayBuffer, tab, text, fontSize, activeColor, opacity, rotation, position,
      wmImage, imageScale, imageOpacity, imagePosition, imageRotation,
      pageMode, customRange, placement]);

  // pdf-lib coordinate system: origin at bottom-left, y goes UP.
  // drawText x,y = baseline-left of the text.
  // The `rotate` option rotates around the x,y point.
  // To center-rotate text, we offset x,y to the center of the text bounding box,
  // then adjust the draw coordinates accordingly.

  function drawTextWatermarkPdfLib(page, font, pw, ph) {
    const { r, g, b } = hexToRgbValues(activeColor);
    const tw = font.widthOfTextAtSize(text, fontSize);
    const th = font.heightAtSize(fontSize);
    const rad = (rotation * Math.PI) / 180;

    // Bug 9: When placement is 'behind', reduce opacity further for visual distinction
    const effectiveOpacity = placement === 'behind' ? opacity * 0.3 : opacity;

    const drawOpts = (cx, cy) => {
      const cosR = Math.cos(rad);
      const sinR = Math.sin(rad);
      const ox = -tw / 2;
      const oy = -th / 3;
      const rx = cx + ox * cosR - oy * sinR;
      const ry = cy + ox * sinR + oy * cosR;
      return {
        x: rx,
        y: ry,
        size: fontSize,
        font,
        color: rgb(r, g, b),
        opacity: effectiveOpacity,
        rotate: degrees(rotation),
      };
    };

    if (position === 'tile') {
      const spacingX = tw + 60;
      const spacingY = th + 60;
      for (let cy = -th; cy < ph + th; cy += spacingY) {
        for (let cx = -tw; cx < pw + tw; cx += spacingX) {
          page.drawText(text, drawOpts(cx, cy));
        }
      }
    } else {
      const { cx, cy } = getPdfLibCenter(position, pw, ph, tw, th, rotation);
      page.drawText(text, drawOpts(cx, cy));
    }
  }

  function drawImageWatermarkPdfLib(page, embeddedImage, pw, ph) {
    const scaleFactor = imageScale / 100;
    const maxDim = Math.min(pw, ph);
    const imgAspect = embeddedImage.width / embeddedImage.height;
    let drawW = maxDim * scaleFactor;
    let drawH = drawW / imgAspect;
    if (drawH > maxDim * scaleFactor) {
      drawH = maxDim * scaleFactor;
      drawW = drawH * imgAspect;
    }

    // Bug 9: When placement is 'behind', reduce opacity further for visual distinction
    const effectiveOpacity = placement === 'behind' ? imageOpacity * 0.3 : imageOpacity;

    const drawAt = (cx, cy) => {
      page.drawImage(embeddedImage, {
        x: cx - drawW / 2,
        y: cy - drawH / 2,
        width: drawW,
        height: drawH,
        opacity: effectiveOpacity,
        rotate: degrees(imageRotation),
      });
    };

    if (imagePosition === 'tile') {
      const spacingX = drawW + 40;
      const spacingY = drawH + 40;
      for (let cy = drawH / 2; cy < ph + drawH; cy += spacingY) {
        for (let cx = drawW / 2; cx < pw + drawW; cx += spacingX) {
          drawAt(cx, cy);
        }
      }
    } else {
      const { cx, cy } = getPdfLibCenter(imagePosition, pw, ph, drawW, drawH, imageRotation);
      drawAt(cx, cy);
    }
  }

  // Compute the bounding box of a rotated rectangle
  function getRotatedBounds(w, h, angleDeg) {
    const rad = Math.abs(angleDeg * Math.PI / 180);
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return {
      width: w * cos + h * sin,
      height: w * sin + h * cos,
    };
  }

  // Returns the CENTER point in pdf-lib coords (bottom-left origin, y-up)
  // Uses rotated bounding box for corner positions to prevent clipping
  function getPdfLibCenter(pos, pw, ph, ew, eh, angleDeg = 0) {
    const margin = 40;
    // Use rotated bounds for margin calculations at corners
    const { width: rw, height: rh } = getRotatedBounds(ew, eh, angleDeg);
    switch (pos) {
      case 'top-left': return { cx: margin + rw / 2, cy: ph - margin - rh / 2 };
      case 'top-right': return { cx: pw - margin - rw / 2, cy: ph - margin - rh / 2 };
      case 'bottom-left': return { cx: margin + rw / 2, cy: margin + rh / 2 };
      case 'bottom-right': return { cx: pw - margin - rw / 2, cy: margin + rh / 2 };
      case 'center':
      default:
        return { cx: pw / 2, cy: ph / 2 };
    }
  }

  // ==================== Result actions ====================

  const handleDownload = useCallback(() => {
    if (!result) return;
    const baseName = fileName.replace(/\.pdf$/i, '');
    const url = URL.createObjectURL(result.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}_watermarked.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }, [result, fileName]);

  const openInViewer = useCallback(() => {
    if (!result) return;
    const baseName = fileName.replace(/\.pdf$/i, '');
    const file = new File([result.blob], `${baseName}_watermarked.pdf`, { type: 'application/pdf' });
    navigate('/viewer', { state: { file } });
  }, [result, fileName, navigate]);

  // ==================== Render ====================

  return (
    <ProGate feature="watermark">
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Watermark</h1>
            <p className="text-sm text-gray-500">Add text or image watermarks to your PDF</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {/* Drop zone */}
        {!pdfDoc && (
          <>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onClick={handleBrowse}
              className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all mb-6 ${
                isDragging ? 'border-brand-blue bg-brand-light/30 scale-[1.01]' : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
              }`}
            >
              <Upload size={36} className={`mx-auto mb-3 ${isDragging ? 'text-brand-blue' : 'text-gray-400'}`} />
              <p className="text-lg font-medium text-gray-700">Drop a PDF here, or click to browse</p>
              <p className="text-sm text-gray-400 mt-1">Select a PDF to add watermarks</p>
            </div>
            <CloudSourceHint
              onPicked={(r) => loadFile(new File([r.buffer], r.name, { type: 'application/pdf' }))}
              className="mb-6"
            />
            {error && <p role="alert" className="text-red-600 text-sm -mt-3 mb-6">{error}</p>}
          </>
        )}

        {pdfDoc && !result && (
          <>
            {/* File info */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex items-center gap-3">
              <div className="w-8 h-11 rounded border border-gray-200 overflow-hidden shrink-0 bg-white">
                <PageCanvas pdfDoc={pdfDoc} pageNumber={1} scale={0.08} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
                <p className="text-xs text-gray-500">
                  {numPages} page{numPages !== 1 ? 's' : ''} · {formatFileSize(fileSize)}
                </p>
              </div>
              <button onClick={handleBrowse} className="text-sm text-brand-blue hover:underline font-medium">
                Change file
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Settings */}
              <div className="space-y-4">
                {/* Tabs */}
                <div className="flex gap-1 bg-white rounded-xl border border-gray-200 p-1">
                  <button
                    onClick={() => setTab('text')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      tab === 'text' ? 'bg-brand-blue text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Type size={16} />
                    Text Watermark
                  </button>
                  <button
                    onClick={() => setTab('image')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      tab === 'image' ? 'bg-brand-blue text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Image size={16} />
                    Image Watermark
                  </button>
                </div>

                {/* Text watermark settings */}
                {tab === 'text' && (
                  <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                    {/* Text input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Text</label>
                      <input
                        type="text"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Enter watermark text"
                        className="w-full h-10 px-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue"
                      />
                    </div>

                    {/* Font size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Font Size: {fontSize}px
                      </label>
                      <input
                        type="range"
                        min={12}
                        max={120}
                        value={fontSize}
                        onChange={(e) => setFontSize(Number(e.target.value))}
                        className="w-full accent-brand-blue"
                      />
                      <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                        <span>12</span><span>120</span>
                      </div>
                    </div>

                    {/* Color */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Color</label>
                      <div className="flex items-center gap-2 flex-wrap">
                        {COLORS.map((c) => (
                          <button
                            key={c.hex}
                            onClick={() => setColor(c.hex)}
                            className={`w-7 h-7 rounded-full border-2 transition-transform ${
                              color === c.hex ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-105'
                            }`}
                            style={{ backgroundColor: c.hex }}
                            title={c.label}
                          />
                        ))}
                        <div className="flex items-center gap-1.5 ml-1">
                          <input
                            type="color"
                            value={customColor}
                            onChange={(e) => { setCustomColor(e.target.value); setColor('custom'); }}
                            className="w-7 h-7 rounded border border-gray-300 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={color === 'custom' ? customColor : color}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (/^#[0-9a-fA-F]{6}$/.test(v)) {
                                setCustomColor(v);
                                setColor('custom');
                              }
                            }}
                            placeholder="#000000"
                            className="w-20 h-7 px-2 text-xs border border-gray-300 rounded focus:outline-none focus:border-brand-blue"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Opacity */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Opacity: {Math.round(opacity * 100)}%
                      </label>
                      <input
                        type="range"
                        min={10}
                        max={100}
                        value={Math.round(opacity * 100)}
                        onChange={(e) => setOpacity(Number(e.target.value) / 100)}
                        className="w-full accent-brand-blue"
                      />
                    </div>

                    {/* Rotation */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Rotation: {rotation}°
                      </label>
                      <input
                        type="range"
                        min={-180}
                        max={180}
                        value={rotation}
                        onChange={(e) => setRotation(Number(e.target.value))}
                        className="w-full accent-brand-blue"
                      />
                      <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                        <span>-180°</span><span>0°</span><span>180°</span>
                      </div>
                    </div>

                    {/* Position */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Position</label>
                      <select
                        value={position}
                        onChange={(e) => setPosition(e.target.value)}
                        className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue bg-white"
                      >
                        {POSITIONS.map((p) => (
                          <option key={p.id} value={p.id}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Image watermark settings */}
                {tab === 'image' && (
                  <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                    {/* Image upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Watermark Image</label>
                      {wmImage ? (
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <img src={wmImage.url} alt="Watermark" className="w-12 h-12 object-contain rounded border border-gray-200 bg-white" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{wmImage.name}</p>
                            <p className="text-xs text-gray-500">{wmImage.width} x {wmImage.height}px</p>
                          </div>
                          <button
                            onClick={handleBrowseImage}
                            className="text-sm text-brand-blue hover:underline font-medium"
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={handleBrowseImage}
                          className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-brand-blue hover:bg-gray-50 transition-all"
                        >
                          <Image size={24} className="mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-600">Upload PNG, JPG, or SVG</p>
                          <p className="text-xs text-gray-400 mt-0.5">PNG with transparency works best</p>
                        </button>
                      )}
                    </div>

                    {wmImage && (
                      <>
                        {/* Scale */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Scale: {imageScale}%
                          </label>
                          <input
                            type="range"
                            min={10}
                            max={200}
                            value={imageScale}
                            onChange={(e) => setImageScale(Number(e.target.value))}
                            className="w-full accent-brand-blue"
                          />
                          <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                            <span>10%</span><span>200%</span>
                          </div>
                        </div>

                        {/* Opacity */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Opacity: {Math.round(imageOpacity * 100)}%
                          </label>
                          <input
                            type="range"
                            min={10}
                            max={100}
                            value={Math.round(imageOpacity * 100)}
                            onChange={(e) => setImageOpacity(Number(e.target.value) / 100)}
                            className="w-full accent-brand-blue"
                          />
                        </div>

                        {/* Position */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Position</label>
                          <select
                            value={imagePosition}
                            onChange={(e) => setImagePosition(e.target.value)}
                            className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue bg-white"
                          >
                            {POSITIONS.map((p) => (
                              <option key={p.id} value={p.id}>{p.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Rotation */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Rotation: {imageRotation}°
                          </label>
                          <input
                            type="range"
                            min={-180}
                            max={180}
                            value={imageRotation}
                            onChange={(e) => setImageRotation(Number(e.target.value))}
                            className="w-full accent-brand-blue"
                          />
                          <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                            <span>-180°</span><span>0°</span><span>180°</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Apply options */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">Apply Options</h3>

                  {/* Page range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Apply to</label>
                    <div className="flex gap-2 flex-wrap">
                      {PAGE_MODES.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => setPageMode(m.id)}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                            pageMode === m.id
                              ? 'border-brand-blue bg-brand-light/50 text-brand-blue font-medium'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                    {pageMode === 'custom' && (
                      <input
                        type="text"
                        value={customRange}
                        onChange={(e) => setCustomRange(e.target.value)}
                        placeholder="e.g. 1-5, 8, 12"
                        className="mt-2 w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue"
                      />
                    )}
                  </div>

                  {/* Placement */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Placement</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPlacement('on-top')}
                        className={`flex-1 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                          placement === 'on-top'
                            ? 'border-brand-blue bg-brand-light/50 text-brand-blue font-medium'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        On Top of Content
                      </button>
                      <button
                        onClick={() => setPlacement('behind')}
                        className={`flex-1 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                          placement === 'behind'
                            ? 'border-brand-blue bg-brand-light/50 text-brand-blue font-medium'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        Behind Content
                      </button>
                    </div>
                  </div>
                </div>

                {/* Apply button */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

                  {processing && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Loader2 size={16} className="animate-spin text-brand-blue" />
                        <span className="text-sm text-gray-600">Applying watermark… {Math.round(progress * 100)}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-brand-blue rounded-full transition-all duration-200" style={{ width: `${progress * 100}%` }} />
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleApply}
                    disabled={processing || (tab === 'text' && !text.trim()) || (tab === 'image' && !wmImage)}
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Droplets size={18} />
                    Apply Watermark
                  </button>
                </div>
              </div>

              {/* Right: Live preview */}
              <div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Preview (Page 1)</p>
                  <div className="bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                    <canvas
                      ref={previewCanvasRef}
                      className="max-w-full h-auto"
                      style={{ display: 'block' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Result */}
        {result && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="text-center mb-6">
              <Droplets size={40} className="mx-auto mb-3 text-brand-blue" />
              <p className="text-lg font-semibold text-gray-900 mb-1">Watermark Applied</p>
              <p className="text-sm text-gray-500">
                {formatFileSize(result.size)}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={openInViewer}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors"
              >
                <Eye size={16} />
                Open in Viewer
              </button>
              <button
                onClick={handleDownload}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                <Download size={16} />
                Download
              </button>
            </div>

            <button
              onClick={() => setResult(null)}
              className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 py-2 transition-colors"
            >
              Adjust settings and re-apply
            </button>
          </div>
        )}
      </div>
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
    </ProGate>
  );
}
